import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';

import Content from '../../components/Common/ScreenLayout/Content';
import Dashboard from '../../components/Dashboard';
import appRoutes from '../../navigation/appRoutes';
import { CanvasLoading } from '../../components/Skeleton';
import Row from '../../components/Common/Row';
import Typography from 'kingpin/atoms/Typography';
import ResourceTopBar, {
  ResourceFavouriteButton,
} from '../../components/ResourceTopBar';
import InnerContent from '../InnerContent';
import { ScreenWrapper } from '../../navigation/styles';
import AccountPickerContext from '../../contexts/AccountPickerContext';
import { DASHBOARD_MENU_PORTAL_DIV_ID } from './constants';
import STORE from '../../store';
import { EXCLUDE_HEIGHT } from '../../constants';
import ShareContentButton from '../../components/ShareContentButton';
import OpenInsightsButton from '../../components/InsightsChat/OpenInsightsButton';
import PortalAccountsGate from '../../components/PortalAccountsGate';
import V3ContentBackButton from '../V3/Content/V3ContentBackButton';
import V3PublishOrShareButton from '../V3/Content/V3PublishOrShareButton';
import V3PrivateIndicator from '../V3/Content/V3PrivateIndicator';
import AiGeneratedBadge from '../../components/AiGeneratedBadge';
import { UIVersionContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
`;

export const useDashboard = (dashboardId: string | undefined) => {
  const navigate = useNavigate();
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [dashboard, setDashboard] = useState<DashboardType>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!selectedAccountId || !dashboardId) return;
    let isActive = true;

    const unsubscribe = STORE.getDashboardsRef({ accountId: selectedAccountId })
      .doc(dashboardId)
      .onSnapshot((doc) => {
        if (!isActive) return;
        if (!doc.exists) {
          setIsLoading(false);
          navigateRef.current(appRoutes.loggedIn.dashboards);
          return;
        }
        setDashboard(doc.data() as DashboardType);
        setIsLoading(false);
      });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [selectedAccountId, dashboardId]);

  return {
    dashboard,
    isLoading,
  };
};

export const DashboardTopBar = ({
  dashboard,
}: {
  dashboard: DashboardType | undefined;
}) => {
  const { isV3 } = useContext(UIVersionContext);
  if (!dashboard) {
    return null;
  }

  return (
    <ResourceTopBar
      isCanvas
      className={isV3 ? '' : `${EXCLUDE_HEIGHT}-${dashboard.id}`}
    >
      <Row spaceBetween style={{ height: '100%', width: '100%' }} centerAlign>
        <Row centerAlign style={{ height: '100%', gap: 8 }}>
          <V3ContentBackButton />
          <Typography.Header type="H3">
            {dashboard ? dashboard.name : '...'}
          </Typography.Header>
          {dashboard && (
            <div>
              <ResourceFavouriteButton
                type="dashboard"
                typeId={dashboard.id}
                name={dashboard.name}
              />
            </div>
          )}
          <V3PrivateIndicator />
          {dashboard.isAIGenerated && <AiGeneratedBadge />}
        </Row>
        <Row centerAlign style={{ gap: 8 }}>
          <OpenInsightsButton />
          {!!dashboard && (
            <PortalAccountsGate>
              <ShareContentButton
                typeId={dashboard.id}
                type={'dashboard'}
                contentName={dashboard.name}
                hasDivider
              />
            </PortalAccountsGate>
          )}
          <V3PublishOrShareButton />
          <div id={DASHBOARD_MENU_PORTAL_DIV_ID} />
        </Row>
      </Row>
    </ResourceTopBar>
  );
};

const DashboardShow = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const { dashboard, isLoading } = useDashboard(dashboardId);
  const navigate = useNavigate();
  const { isV3 } = useContext(UIVersionContext);

  return (
    <ScreenWrapper>
      <Content>
        <div style={{ flex: 1 }}>
          <Wrapper>
            <InnerContent style={{ height: '100vh' }}>
              <DashboardTopBar dashboard={dashboard} />
              <React.Fragment>
                {isLoading && <CanvasLoading />}
                {!isLoading && dashboard && (
                  <Dashboard
                    dashboard={dashboard}
                    key={dashboardId}
                    onDeleted={isV3 ? () => navigate('/content') : undefined}
                  />
                )}
              </React.Fragment>
            </InnerContent>
          </Wrapper>
        </div>
      </Content>
    </ScreenWrapper>
  );
};

export default DashboardShow;
